import Controller from '@ember/controller';
import { isPresent, isEmpty, compare } from '@ember/utils';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';

import config from 'cio/config/environment';

import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import LayoutModel from 'cio/models/layout';
import DS from 'ember-data';

const ITEMS_PER_PAGE = 25;

const DEFAULTS = {
  archived: false,
  page: 1,
  search: '',
};

export default class LayoutsController extends Controller {
  queryParams = ['search', 'page', 'archived'];
  @tracked archived = DEFAULTS.archived;
  @tracked page = DEFAULTS.page;
  @tracked search = DEFAULTS.search;

  declare model: DS.PromiseArray<LayoutModel>;

  itemsPerPage = ITEMS_PER_PAGE;

  get layouts() {
    return this.model.filter((layout: LayoutModel) => layout.archived === this.archived);
  }

  get sortedLayouts() {
    return this.layouts.toArray().sort((a: LayoutModel, b: LayoutModel) => {
      let compareValue = compare(b.emailsCount, a.emailsCount);

      if (compareValue !== 0) {
        return compareValue;
      }

      return compare(a.name, b.name);
    });
  }

  get results() {
    let search = this.search.trim().toLowerCase();
    let layouts = this.sortedLayouts;

    if (isEmpty(search)) {
      return layouts;
    }

    return layouts.filter((layout: LayoutModel) => {
      return layout.name.toLowerCase().indexOf(search) !== -1;
    });
  }

  get pageResults() {
    let start = (this.page - 1) * this.itemsPerPage;
    let end = start + this.itemsPerPage;

    return isPresent(this.results) ? this.results.slice(start, end) : this.results;
  }

  get counts() {
    let { archived, total } = (this.model as any).meta ?? {};
    let active = total - archived;

    // when switching between "active" and "archived" tabs route model is refreshed
    // this turns this.layouts into empty array for split second during transition, causing invalid tab counts to be shown
    // this check detects and uses previously computed counts when applicable
    let isRefreshingModel = this.model.length !== (this.archived ? archived : active);
    if (isRefreshingModel) {
      return { active, archived };
    }

    if (this.archived) {
      return { active: total - this.layouts.length, archived: this.layouts.length };
    } else {
      return { active: this.layouts.length, archived: total - this.layouts.length };
    }
  }

  get emptyLayoutOnly() {
    let { total } = (this.model as any).meta;
    let layout = this.model.firstObject;

    if (total !== 1 || layout == null) {
      return false;
    }

    if (layout.name === 'Empty Layout' && layout.id === '1' && !layout.archived) {
      return true;
    }

    return false;
  }

  get hasNoLayouts() {
    return (this.model as any).meta.total === 0;
  }

  get blankSlateText() {
    if (isPresent(this.search)) {
      return 'No results found';
    } else if (this.archived) {
      return 'No archived layouts';
    } else {
      return 'No active layouts';
    }
  }

  setDefaults() {
    this.archived = DEFAULTS.archived;
    this.page = DEFAULTS.page;
    this.search = DEFAULTS.search;
  }

  @action
  setSearch(search: string) {
    this.search = search;
    this.resetPagination();
  }

  @action
  resetPagination() {
    this.page = DEFAULTS.page;
  }

  setSearchTask = task({ restartable: true }, async (event: FlyKeyboardEvent<HTMLInputElement>) => {
    await timeout(config.APP.debounceDuration);
    this.setSearch(event.target.value);
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    layouts: LayoutsController;
  }
}
